import { useEffect } from 'react'
import { price as fmtPrice } from '../format'
import type { PnlRow } from '../pnl'

/**
 * PnL paylaşım kartı — ekran görüntüsü alıp paylaşmak İÇİN tasarlandı
 * (kullanıcı isteği 2026-07-29, Binance'in PnL kartı esinlemesi; tasarım
 * revizyonu aynı gün: marka ana sayfayla aynı — ikon + "GİP Simülasyon
 * Merkezi", kontrat "GG.AA.YYYY SS" biçiminde, PTF/SMF/Talimat üçlüsü).
 *
 * İndirme/kopyalama düğmesi bilerek yok: amaç SS. "GÖLGE" rozeti bilinçli:
 * görüntü gerçek piyasa işlemi sanılmasın.
 */

const tlFmt = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
const mwhFmt = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})
const talimatFmt = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 })

const AYLAR = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
]

function tarihUzun(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  return `${d} ${AYLAR[(m ?? 1) - 1]} ${y}`
}

/** +imzalı TL — kart kâr/zararı tek bakışta söylemeli. */
function imzali(v: number): string {
  return (v > 0 ? '+' : '') + tlFmt.format(v)
}

export function PnlShareCard({ r, onClose }: { r: PnlRow; onClose: () => void }) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onClose])

  const yon = r.pnlTl == null || r.pnlTl === 0 ? 'notr' : r.pnlTl > 0 ? 'poz' : 'neg'

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className={`share-card share-${yon}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="PnL kartı"
      >
        <button type="button" className="share-close" onClick={onClose} aria-label="Kapat">
          ×
        </button>

        <div className="share-brand">
          {/* Ana sayfadaki marka ikonu — gradyan kendi tanımıyla (id çakışmasın). */}
          <span className="share-logo" aria-hidden="true">
            <svg viewBox="0 0 26 26" width="22" height="22">
              <defs>
                <linearGradient id="shareGrad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#6aa9f0" />
                  <stop offset="100%" stopColor="#3a6fbd" />
                </linearGradient>
              </defs>
              <rect className="mark-ghost" x="3.5" y="8.5" width="14" height="14" rx="4" />
              <rect fill="url(#shareGrad)" x="8.5" y="3.5" width="14" height="14" rx="4" />
            </svg>
          </span>
          <span className="share-name">
            <b>GİP</b> Simülasyon Merkezi
          </span>
          <span className="share-tag">Gölge</span>
        </div>

        <div className="share-head">
          {/* Tam kontrat adı; son iki hane (teslim saati) vurgulu ve büyük. */}
          <span className="share-contract-chip">
            {r.contract.slice(0, -2)}
            <b>{r.contract.slice(-2)}</b>
          </span>
          <div className="share-prices">
            <div className="sp">
              <span>PTF</span>
              <b>{fmtPrice(r.ptf)}</b>
            </div>
            {/* SMF açıklanmadıysa düz "—": tablodaki geçici (SMF=PTF) varsayımı
                paylaşılan karta taşınmaz (kullanıcı isteği). */}
            <div className="sp">
              <span>SMF</span>
              <b>{r.smfProvisional ? '—' : fmtPrice(r.smf)}</b>
            </div>
            <div className="sp">
              <span>Talimat</span>
              <b>
                {r.talimat == null
                  ? '—'
                  : (r.talimat > 0 ? '+' : '') + talimatFmt.format(r.talimat)}
              </b>
            </div>
          </div>
        </div>

        <div className="share-pnl">
          <span className="share-pnl-label">PnL</span>
          <div className="share-pnl-value">
            {r.pnlTl == null ? '—' : imzali(r.pnlTl)} <small>TL</small>
          </div>
          <span className="share-pnl-sub">
            {r.pnlTl == null
              ? 'kontrat açık — sonuç bekleniyor'
              : `${imzali(r.pnlPerMwh ?? 0)} TL/MWh · %${tlFmt.format(r.pnlPctPtf ?? 0)} PTF`}
          </span>
        </div>

        <div className="share-stats">
          <div className="share-stat">
            <span>Alış</span>
            <b>{mwhFmt.format(r.buy.mwh)} MWh</b>
            <i>AOF {fmtPrice(r.buy.aof)}</i>
          </div>
          <div className="share-stat">
            <span>Satış</span>
            <b>{mwhFmt.format(r.sell.mwh)} MWh</b>
            <i>AOF {fmtPrice(r.sell.aof)}</i>
          </div>
        </div>

        <div className="share-foot">
          <span className="share-foot-url">shadow.gainenerji.com</span>
          <span>{tarihUzun(r.date)}</span>
        </div>
      </div>
    </div>
  )
}
